import * as React from "react"

import { Badge, cn } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useAccountingTrialBalanceQuery } from "@/features/finance/services/finance-query"
import { useFinanceInvestigationSignalsQuery } from "@/features/finance/services/finance-investigation-query"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { useFinanceOverviewScope } from "@/features/finance/overview/context/finance-overview-context"
import { formatCompactNumber } from "@/features/finance/overview/lib/overview-format"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"

interface AuditData {
  balanced: boolean
  missingEvidence: number
  reviewable: number
  signalsLoaded: boolean
}

type AuditStatus = "balanced" | "review" | "exceptions"

/**
 * Audit & Assurance — surfaces finance integrity signals the backend can actually prove. The headline
 * status derives ONLY from the real trial-balance `balanced` flag plus proven investigation signals
 * (missing evidence, reversals, voids) — never from the mere absence of a frontend error. Current-state
 * (period-independent). A dedicated integrity-check endpoint does not exist, so those signals are the
 * full provable set; anything richer is deferred.
 */
export function AuditAssuranceWidget() {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const go = useOverviewNav()

  const trialBalance = useAccountingTrialBalanceQuery(
    scope.workspaceId ? { workspaceId: scope.workspaceId, branchId: scope.branchId ?? undefined } : null,
  )
  const signals = useFinanceInvestigationSignalsQuery(
    scope.workspaceId
      ? {
          workspaceId: scope.workspaceId,
          branchId: scope.branchId ?? undefined,
          scope: scope.context === "parish" ? "PARISH" : "BRANCH",
        }
      : null,
  )

  const data = React.useMemo<AuditData | undefined>(() => {
    if (!trialBalance.data) return undefined
    const s = signals.data
    return {
      balanced: Boolean(trialBalance.data.balanced),
      missingEvidence: s?.missingEvidence.count ?? 0,
      reviewable: (s?.reversed.count ?? 0) + (s?.voided.count ?? 0),
      signalsLoaded: Boolean(s),
    }
  }, [trialBalance.data, signals.data])

  const refetch = React.useCallback(() => {
    void trialBalance.refetch()
    void signals.refetch()
  }, [signals, trialBalance])

  useWidgetMenu({
    refresh: refetch,
    viewDetails: go.canNavigateTo(OverviewRoutes.investigations) ? () => go(OverviewRoutes.investigations) : undefined,
  })

  return (
    <WidgetBoundary
      isLoading={trialBalance.isLoading}
      isError={trialBalance.isError}
      isFetching={trialBalance.isFetching || signals.isFetching}
      data={data}
      skeleton={<AuditSkeleton />}
      errorTitle={t("widgets.auditAssurance.error")}
      onRetry={refetch}
      retryLabel={t("widgets.common.retry")}
    >
      {(d) => {
        const openIssues = d.missingEvidence + d.reviewable
        const status: AuditStatus = !d.balanced ? "exceptions" : d.signalsLoaded && openIssues > 0 ? "review" : "balanced"
        const badge = STATUS_BADGE[status]
        return (
          <div className="space-y-3">
            <Badge variant={badge.variant} className={badge.className}>
              {t(`widgets.auditAssurance.status.${status}`)}
            </Badge>

            <ul className="space-y-1.5 text-sm">
              <IntegrityRow
                label={t("widgets.auditAssurance.rows.journalsBalanced")}
                value={t(d.balanced ? "widgets.auditAssurance.yes" : "widgets.auditAssurance.no")}
                tone={d.balanced ? "muted" : "destructive"}
              />
              <IntegrityRow
                label={t("widgets.auditAssurance.rows.missingEvidence")}
                value={formatCompactNumber(d.missingEvidence)}
                tone={d.missingEvidence > 0 ? "warning" : "muted"}
                onClick={go.canNavigateTo(OverviewRoutes.investigations) ? () => go(OverviewRoutes.investigations) : undefined}
              />
              <IntegrityRow
                label={t("widgets.auditAssurance.rows.reviewable")}
                value={formatCompactNumber(d.reviewable)}
                tone={d.reviewable > 0 ? "warning" : "muted"}
                onClick={go.canNavigateTo(OverviewRoutes.investigations) ? () => go(OverviewRoutes.investigations) : undefined}
              />
            </ul>
          </div>
        )
      }}
    </WidgetBoundary>
  )
}

const STATUS_BADGE: Record<AuditStatus, { variant: "success" | "outline" | "destructive"; className?: string }> = {
  balanced: { variant: "success" },
  review: { variant: "outline", className: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400" },
  exceptions: { variant: "destructive" },
}

function IntegrityRow({
  label,
  value,
  tone,
  onClick,
}: {
  label: string
  value: string
  tone: "muted" | "warning" | "destructive"
  onClick?: () => void
}) {
  const valueClass = cn(
    "shrink-0 font-medium tabular-nums",
    tone === "warning" && "text-amber-600 dark:text-amber-400",
    tone === "destructive" && "text-destructive",
    tone === "muted" && "text-muted-foreground",
  )
  const content = (
    <>
      <span className="min-w-0 flex-1 truncate text-muted-foreground">{label}</span>
      <span className={valueClass}>{value}</span>
    </>
  )
  return (
    <li>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="flex w-full items-center justify-between gap-3 text-left transition-colors hover:text-primary"
        >
          {content}
        </button>
      ) : (
        <div className="flex items-center justify-between gap-3">{content}</div>
      )}
    </li>
  )
}

function AuditSkeleton() {
  return (
    <div className="space-y-3">
      <div className="h-5 w-28 animate-pulse rounded-md bg-muted motion-reduce:animate-none" />
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="h-3.5 w-32 animate-pulse rounded bg-muted motion-reduce:animate-none" />
            <div className="h-3.5 w-8 animate-pulse rounded bg-muted motion-reduce:animate-none" />
          </div>
        ))}
      </div>
    </div>
  )
}
